import classNames from 'classnames'
import React, { useContext, useEffect } from 'react'
import {
  Maybe,
  SanityBasicImage,
  SanityPhoto,
} from '../../../types/generated/graphql-types'
import { HeaderDataContext } from '../../context/HeaderContext'
import { generateImagesPreloadData } from '../../utilities/imagePropUtils'
import { getFonts } from './Fonts'
import { Seo, SeoProps } from './Seo'

export interface DocumentHeadProps extends SeoProps {
  language?: string
  pageHasStickyMenu: boolean
  jsonLD?: Array<{ [key: string]: string }>
  preloadImages?: Array<SanityBasicImage | Maybe<SanityPhoto> | undefined>
}

export const DocumentHead = ({
  language,
  pageHasStickyMenu,
  title,
  jsonLD,
  preloadImages,
  ...seoProps
}: DocumentHeadProps) => {
  const headerHeight = useContext(HeaderDataContext)

  let scrollPadding = headerHeight + 30

  if (pageHasStickyMenu) {
    scrollPadding += 75
  }

  // Note: Setting style on the <html> element does not seem to work using the Gatsby head API, so just set it client side
  // in a useEffect instead (a base scroll-padding-top value is provided by a className so that it's available in SSR).
  useEffect(() => {
    document.documentElement.setAttribute(
      'style',
      `scroll-padding-top: ${scrollPadding}px;`
    )
  }, [scrollPadding])

  const { fontClassName, fontScriptElement } = getFonts()

  return (
    <>
      <html
        className={classNames(
          fontClassName,
          pageHasStickyMenu ? 'scroll-pt-[195px]' : 'scroll-pt-[120px]'
        )}
        lang={language ?? 'en'}
      />

      <title>{title}</title>

      <link rel="dns-prefetch" href="https://use.typekit.net/" crossOrigin="anonymous" />
      <link rel="preconnect" href="https://use.typekit.net/" crossOrigin="anonymous" />

      {fontScriptElement}

      <Seo {...seoProps} title={title} jsonLD={jsonLD} />

      {process.env.GATSBY_SANITY_DATASET === 'eqt-athens-prod' && (
        <meta
          name="google-site-verification"
          content="fGPdsYFLTnYiDXx3yHF7kWa-VwoKm0TUimYLuxVMhVY"
        />
      )}

      {process.env.GATSBY_SANITY_DATASET === 'eqt-athens-prod' && (
        <meta
          name="facebook-domain-verification"
          content="a50ztqj839qbmhvcwggwfpn5br89mx"
        />
      )}

      {generateImagesPreloadData(preloadImages)?.map(image => (
        <link
          key={image.source}
          rel="preload"
          href={image.source}
          as="image"
          type={image.type}
        />
      ))}
    </>
  )
}
